// Stream Viewer : affichage structuré du partage d'écran en salon.
// En MP, la vidéo va dans mpc-stream-zone (géré dans peers.js).
// En salon, ce module crée/gère un panel stylé au-dessus du chat.

import { t } from '../core/i18n.js';
import { notifInApp } from '../ui/inAppNotif.js';
import { state } from '../core/state.js';
import { rtcState } from './rtcState.js';

let _viewerEl    = null;
let _videoEl     = null;
let _pseudo      = null;
let _collapsed   = false;

// ── Construction du panel ────────────────────────────────────────────────────

function _buildViewer() {
    if (_viewerEl) return;

    _viewerEl = document.createElement('div');
    _viewerEl.id = 'stream-viewer';
    _viewerEl.innerHTML = `
        <div id="sv-header">
            <span id="sv-icon">📺</span>
            <span id="sv-title">Partage d'écran</span>
            <div id="sv-controls">
                <button id="sv-btn-spectateurs" title="Spectateurs" style="display:none">
                    <i class="fas fa-eye"></i> <span id="sv-spectateurs-count">0</span>
                </button>
                <button id="sv-btn-pip"        title="Picture-in-Picture"><i class="fas fa-external-link-alt"></i></button>
                <button id="sv-btn-fullscreen" title="Plein écran"><i class="fas fa-expand"></i></button>
                <button id="sv-btn-leave"      title="Quitter le stream" style="display:none"><i class="fas fa-times-circle"></i></button>
                <button id="sv-btn-toggle"     title="Réduire">▼</button>
            </div>
        </div>
        <div id="sv-spectateurs-list" style="display:none"></div>
        <div id="sv-video-wrap">
            <div id="sv-close-overlay">
                <button id="sv-btn-close-stream" title="Quitter le stream">
                    <span class="sv-tv-icon"><i class="fas fa-tv"></i></span>
                </button>
            </div>
        </div>
    `;

    // Insérer avant le wrapper de messages
    const chatView = document.getElementById('chat-view');
    if (chatView) chatView.insertBefore(_viewerEl, chatView.firstChild);
    else document.body.appendChild(_viewerEl);

    document.getElementById('sv-btn-toggle').addEventListener('click', _toggleCollapse);
    document.getElementById('sv-btn-fullscreen').addEventListener('click', _goFullscreen);
    document.getElementById('sv-btn-pip').addEventListener('click', _goPiP);
    document.getElementById('sv-btn-spectateurs').addEventListener('click', _toggleSpectateursList);
    document.getElementById('sv-btn-close-stream').addEventListener('click', fermerStreamViewer);
    document.getElementById('sv-btn-leave').addEventListener('click', fermerStreamViewer);

    // Hover JS sur sv-video-wrap (le :hover CSS ne marche pas sur <video> dans certains browsers)
    const overlay  = document.getElementById('sv-close-overlay');
    const videoWrap = document.getElementById('sv-video-wrap');
    videoWrap.addEventListener('mouseenter', () => { overlay.style.opacity = '1'; overlay.style.pointerEvents = 'all'; });
    videoWrap.addEventListener('mouseleave', () => { overlay.style.opacity = '0'; overlay.style.pointerEvents = 'none'; });
}

function _toggleSpectateursList() {
    const list = document.getElementById('sv-spectateurs-list');
    if (list) list.style.display = list.style.display === 'none' ? 'block' : 'none';
}

export function majSpectateurs(liste) {
    const btn = document.getElementById('sv-btn-spectateurs');
    const count = document.getElementById('sv-spectateurs-count');
    const list = document.getElementById('sv-spectateurs-list');
    if (!btn || !count || !list) return;
    if (liste.length === 0) {
        btn.style.display = 'none';
        list.style.display = 'none';
    } else {
        btn.style.display = 'inline-flex';
        count.textContent = liste.length;
        list.innerHTML = liste.map(p => `<span class="sv-spectateur-item">${p}</span>`).join('');
    }
}

// ── API publique ─────────────────────────────────────────────────────────────

export function attachSalonStream(pseudo, videoEl) {
    _buildViewer();
    _pseudo  = pseudo;
    _videoEl = videoEl;
    _collapsed = false;

    const wrap = document.getElementById('sv-video-wrap');
    if (wrap && !wrap.contains(videoEl)) {
        wrap.appendChild(videoEl);
        videoEl.style.display = 'block';
    }

    document.getElementById('sv-title').textContent = `${pseudo} partage son écran`;
    _viewerEl.classList.remove('sv-collapsed');
    _viewerEl.style.display = 'flex';

    // Bouton quitter visible uniquement pour les spectateurs (pas le streamer)
    const leaveBtn = document.getElementById('sv-btn-leave');
    if (leaveBtn) leaveBtn.style.display = pseudo !== state.monPseudo ? 'inline-flex' : 'none';

    // Notif in-app si ce n'est pas moi
    if (pseudo !== state.monPseudo) {
        const infos = state.utilisateurs[pseudo] || {};
        notifInApp({
            titre:     `📺 ${pseudo}`,
            texte:     'a commencé à partager son écran',
            avatarUrl: infos.avatar_url,
            couleur:   infos.couleur,
            onClick:   () => _expand(),
        });
    }
}

export function fermerStreamViewer() {
    if (!_viewerEl) return;
    _stopPipLoop();
    _viewerEl.style.display = 'none';
    if (_videoEl) { _videoEl.style.display = 'none'; }
    if (typeof window._onFermerStream === 'function') window._onFermerStream();
    _pseudo  = null;
    _videoEl = null;
}

export function detachSalonStream(pseudo) {
    if (!_viewerEl) return;
    if (pseudo && _pseudo !== pseudo) {
        if (_videoEl && _videoEl.isConnected) return;
    }
    _stopPipLoop();
    _viewerEl.style.display = 'none';
    _pseudo  = null;
    _videoEl = null;
}

// ── Contrôles internes ───────────────────────────────────────────────────────

function _toggleCollapse() {
    if (!_viewerEl) return;
    _collapsed = !_collapsed;
    _viewerEl.classList.toggle('sv-collapsed', _collapsed);
    const btn = document.getElementById('sv-btn-toggle');
    if (btn) btn.textContent = _collapsed ? '▲' : '▼';
}

function _expand() {
    if (_collapsed) _toggleCollapse();
    _viewerEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function _goFullscreen() {
    if (!_videoEl) return;
    if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
    } else {
        _videoEl.requestFullscreen?.().catch(() => {});
    }
}

// ── PiP composite : stream + avatars vocaux ──────────────────────────────────

const PIP_W  = 1280;
const PIP_H  = 720;
const AV_R   = 42;   // rayon avatar (42px sur 720 → visible dans le PiP)
const AV_GAP = 10;   // espace entre avatars
const AV_PAD = 16;   // marge bord de l'overlay

const _imgCache = {}; // pseudo → HTMLImageElement (pré-chargée)

function _getAvatar(pseudo) {
    const infos = state.utilisateurs[pseudo] || {};
    const url   = infos.avatar_url;
    if (!url || url === 'none' || url === 'undefined') return null;
    if (!_imgCache[pseudo]) {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = url;
        _imgCache[pseudo] = img;
    }
    return _imgCache[pseudo];
}

let _pipTimerId = null;
let _pipCanvas  = null;
let _pipVideo   = null;

function _stopPipLoop() {
    if (_pipTimerId) { clearInterval(_pipTimerId); _pipTimerId = null; }
    if (_pipVideo) {
        _pipVideo.srcObject = null;
        if (document.pictureInPictureElement === _pipVideo) {
            document.exitPictureInPicture().catch(() => {});
        }
        _pipVideo = null;
    }
    _pipCanvas = null;
    Object.keys(_imgCache).forEach(k => delete _imgCache[k]);
}

function _drawPipFrame(ctx) {
    // 1. Fond noir
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, PIP_W, PIP_H);

    // 2. Vidéo letterboxée (videoWidth > 0 = frame disponible)
    if (_videoEl && _videoEl.videoWidth > 0) {
        const vw = _videoEl.videoWidth;
        const vh = _videoEl.videoHeight;
        const scale = Math.min(PIP_W / vw, PIP_H / vh);
        const dw = vw * scale;
        const dh = vh * scale;
        try {
            ctx.drawImage(_videoEl, (PIP_W - dw) / 2, (PIP_H - dh) / 2, dw, dh);
        } catch (_) { /* frame pas encore disponible */ }
    }

    // 3. Overlay avatars vocaux
    // Priorité : salon vocal de l'utilisateur → sinon premier salon avec des membres
    const monSalonId = localStorage.getItem('memoireVocalId');
    let membres = monSalonId ? (rtcState.etatsVocauxActuels[monSalonId] || []) : [];
    if (!membres.length) {
        for (const m of Object.values(rtcState.etatsVocauxActuels)) {
            if (m.length) { membres = m; break; }
        }
    }
    if (!membres.length) return;

    const NOM_H  = 26;   // hauteur réservée pour le pseudo sous l'avatar
    const diam   = AV_R * 2;
    const stride = diam + AV_GAP;
    const totalW = membres.length * stride - AV_GAP;
    const bx     = AV_PAD;
    const by     = PIP_H - diam - NOM_H - AV_PAD * 2;

    // Fond pilule semi-transparent (englobe avatars + noms)
    ctx.fillStyle = 'rgba(13,17,23,0.75)';
    ctx.beginPath();
    ctx.roundRect(bx - AV_PAD / 2, by - AV_PAD / 2, totalW + AV_PAD, diam + NOM_H + AV_PAD, AV_R + 4);
    ctx.fill();

    const now = Date.now();

    membres.forEach((pseudo, i) => {
        const infos   = state.utilisateurs[pseudo] || {};
        const couleur = infos.couleur || '#ffb75e';
        const nom     = infos.nom_affiche || pseudo;
        const cx      = bx + i * stride + AV_R;
        const cy      = by + AV_R;
        const parle   = rtcState.speakingNow.has(pseudo);

        // Anneau "qui parle" : vert pulsant
        if (parle) {
            const pulse = 0.5 + 0.5 * Math.sin(now / 180);
            ctx.beginPath();
            ctx.arc(cx, cy, AV_R + 5, 0, Math.PI * 2);
            ctx.strokeStyle = `rgba(63,185,80,${0.6 + pulse * 0.4})`;
            ctx.lineWidth = 4 + pulse * 2;
            ctx.stroke();
        }

        // Clip circulaire pour l'avatar
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, AV_R, 0, Math.PI * 2);
        ctx.clip();

        const img = _getAvatar(pseudo);
        if (img && img.complete && img.naturalWidth > 0) {
            ctx.drawImage(img, cx - AV_R, cy - AV_R, diam, diam);
        } else {
            ctx.fillStyle = couleur;
            ctx.fillRect(cx - AV_R, cy - AV_R, diam, diam);
            ctx.fillStyle = '#fff';
            ctx.font = `bold ${AV_R}px system-ui`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(nom.charAt(0).toUpperCase(), cx, cy);
        }
        ctx.restore();

        // Bordure couleur thème (fin, sous l'anneau vert si actif)
        if (!parle) {
            ctx.beginPath();
            ctx.arc(cx, cy, AV_R + 2, 0, Math.PI * 2);
            ctx.strokeStyle = couleur;
            ctx.lineWidth = 2.5;
            ctx.stroke();
        }

        // Pseudo avec fond lisible
        const nomCourt = nom.length > 12 ? nom.slice(0, 11) + '…' : nom;
        ctx.font = 'bold 20px system-ui';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const textW = ctx.measureText(nomCourt).width;
        const ty = cy + AV_R + 6;
        ctx.fillStyle = 'rgba(0,0,0,0.6)';
        ctx.beginPath();
        ctx.roundRect(cx - textW / 2 - 4, ty, textW + 8, 22, 4);
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.fillText(nomCourt, cx, ty + 2);
    });
}

function _goPiP() {
    if (!_videoEl) return;

    // Toggle OFF
    if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
        return;
    }

    const canvas = document.createElement('canvas');
    canvas.width  = PIP_W;
    canvas.height = PIP_H;
    _pipCanvas = canvas;

    const ctx = canvas.getContext('2d');

    // setInterval plutôt que requestAnimationFrame : RAF est throttlé à 1fps
    // quand la fenêtre PiP prend le focus, setInterval continue à ~30fps.
    _drawPipFrame(ctx); // première frame immédiate
    _pipTimerId = setInterval(() => {
        if (!_pipCanvas) { clearInterval(_pipTimerId); _pipTimerId = null; return; }
        _drawPipFrame(ctx);
    }, 33);

    // Vidéo PiP alimentée par le canvas
    const video = document.createElement('video');
    video.muted     = true;
    video.srcObject = canvas.captureStream(30);
    _pipVideo = video;

    video.addEventListener('leavepictureinpicture', _stopPipLoop);

    video.play().then(() => {
        video.requestPictureInPicture().catch(err => {
            console.warn('[PiP composite]', err.message);
            _stopPipLoop();
        });
    }).catch(() => _stopPipLoop());
}
